import {Injectable, NgZone} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {GraphMap, GraphNode, GraphNodeLink, GroupedTemplates} from '../interfaces/graph-node.interface';
import {LinkViewMode} from '../services/setting.service';
import {MapUserRoles} from '../../acl/map-user-roles.enum';
import {Job} from '../interfaces/jobs.dto';
import {OtherLinksMode, OtherNodesMode, SmapletComments} from '@maporium-workspace/shared';

@Injectable({ providedIn: 'root' })
export class MapResource {
  private baseUrl= environment.apiUrl + '/map';
  private transferEventSource: EventSource | null = null;

  constructor(private httpClient: HttpClient, private zone: NgZone) {
  }

  findAllForUser(userId: string): Observable<GraphMap[]> {
    return this.httpClient.get<GraphMap[]>(this.baseUrl + '/user/' + userId);
  }

  getAiUsage(mapId: string): Observable<{
    aiCreditsUsed: number;
    aiUsageBySkill: Record<string, { credits: number; count: number; durationMs: number }>;
  }> {
    return this.httpClient.get<{
      aiCreditsUsed: number;
      aiUsageBySkill: Record<string, { credits: number; count: number; durationMs: number }>;
    }>(this.baseUrl + '/ai-usage/' + mapId);
  }

  toggleGuestComments(mapId: string, hasGuestComments: boolean): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + '/toggle-guest-comments/' + mapId, { hasGuestComments });
  }

  findTemplates(): Observable<GroupedTemplates> {
    return this.httpClient.get<GroupedTemplates>(this.baseUrl + '/templates');
  }

  findByIdWithRelation(id: string): Observable<GraphMap> {
    return this.httpClient.get<GraphMap>(this.baseUrl + '/with-users/' + id);
  }

  createForUser(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + '/create', map);
  }

  createDefaultMap(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + '/create-default', map);
  }

  update(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.httpClient.put<GraphMap>(this.baseUrl + '/' + map.id, map);
  }

  duplicate(map: Partial<GraphMap>): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + '/duplicate/', map);
  }

  delete(mapId: string): Observable<void> {
    return this.httpClient.delete<void>(this.baseUrl + '/' + mapId);
  }

  exportMapWithRelationsRecursive(mapId: string): Observable<BlobPart> {
    // @ts-ignore
    return this.httpClient.get<BlobPart>(this.baseUrl + '/export/' + mapId, { responseType: 'blob'});
  }

  copyToMap(nodes: GraphNode[], mapId: string, grandParent?: GraphNode | null): Observable<GraphNode[]> {
    return this.httpClient.post<GraphNode[]>(this.baseUrl + '/copy/' + mapId, {nodes, grandParent});
  }

  importMapFromZip(file: FormData): Observable<Job> {
    return this.httpClient.post<Job>(this.baseUrl + '/import', file);
  }

  createGptMapWithNodeAndEdges(graphNodes: GraphNode[], links: GraphNodeLink[], userId: string) {
    return this.httpClient.post(this.baseUrl + '/gpt/' + userId, {nodes: graphNodes, links});
  }

  shareMap(mapId: string, usersList: { users: { id: string, role: MapUserRoles }[] }) {
    return this.httpClient.post(this.baseUrl + '/share/' + mapId, { users: usersList.users });
  }

  unshareMap(mapId: string) {
    return this.httpClient.post(this.baseUrl + '/unshare/' + mapId, {});
  }

  publicGetMap(mapId: string): Observable<GraphMap> {
    return this.httpClient.get<GraphMap>(environment.apiUrl + '/public/map/' + mapId);
  }

  changeOwner(mapId: string, userId: string) {
    return this.httpClient.post(this.baseUrl + '/owner/transfer/' + mapId, { userId });
  }

  openPublicAccessToMap(mapId: string) {
    return this.httpClient.post(this.baseUrl + '/publish/' + mapId, {});
  }

  closePublicAccessToMap(mapId: string) {
    return this.httpClient.post(this.baseUrl + '/unpublish/' + mapId, {});
  }

  setLinkViewMode(mapId: string, mode: LinkViewMode) {
    return this.httpClient.post(this.baseUrl + `/view-mode/${mapId}/${mode}`, {});
  }

  setOtherLinksMode(mapId: string, mode: OtherLinksMode) {
    return this.httpClient.post(this.baseUrl + `/other-links-mode/${mapId}/${mode}`, {});
  }

  setOtherNodesMode(mapId: string, mode: OtherNodesMode) {
    return this.httpClient.post(this.baseUrl + `/other-nodes-mode/${mapId}/${mode}`, {});
  }

  setSmapletOpening(mapId: string, opening: {storyId: string | null; sceneId: string | null; silent: boolean}): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + `/smaplet-opening/${mapId}`, opening);
  }

  setSmapletView(mapId: string, view: '2d' | '3d'): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + `/smaplet-view/${mapId}`, { view });
  }

  setSmapletAutoOrbit(mapId: string, autoOrbit: boolean): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + `/smaplet-auto-orbit/${mapId}`, { autoOrbit });
  }

  setSmapletComments(mapId: string, comments: SmapletComments): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + `/smaplet-comments/${mapId}`, { comments });
  }

  setAllowWebsiteEmbedding(mapId: string, allow: boolean): Observable<GraphMap> {
    return this.httpClient.post<GraphMap>(this.baseUrl + `/allow-embedding/${mapId}`, { allow });
  }

  getStarterMap(): Observable<GraphMap | null> {
    return this.httpClient.get<GraphMap | null>(this.baseUrl + '/starter-map');
  }

  getOrCreateStarterMap(): Observable<GraphMap> {
    return this.httpClient.get<GraphMap>(this.baseUrl + '/starter-map-or-create');
  }

  getAllToursAndViewsForMap(mapId: string): Observable<{ tours: any[], views: any[] }> {
    return this.httpClient.get<{ tours: any[], views: any[] }>(this.baseUrl + '/tours-views/' + mapId);
  }

  connectToTransferSse(
    mapId: string,
    onMessage: (data: GraphMap) => void,
    onError: (error: any) => void
  ): void {
    if (this.transferEventSource) {
      this.closeSse();
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      if (this.transferEventSource) {
        this.closeSse();
      }
      console.error('No access token available for SSE connection.');
      return;
    }

    this.transferEventSource = new EventSource(`${this.baseUrl}/transfer/events/${mapId}?token=${token}`);


    // Wrap in NgZone to trigger Angular change detection
    this.transferEventSource.onmessage = (event) => {
      this.zone.run(() => onMessage(JSON.parse(event.data)));
    };

    this.transferEventSource.onerror = (error) => {
      this.zone.run(() => {
        onError(error);
        this.closeSse();
        // Attempt a manual reconnect after 5 seconds
        setTimeout(() => {
          this.connectToTransferSse(mapId, onMessage, onError);
        }, 5000);
      });
    };
  }

  closeSse(): void {
    if (this.transferEventSource) {
      this.transferEventSource.close();
      this.transferEventSource = null;
    }
  }
}
